import React, {useEffect, useState} from 'react';
import {Link, useNavigate, useParams} from 'react-router-dom';
import {Col, message, Row, Typography} from 'antd';
import ENDPOINTS from '../../constants/endpoints';
import {ROUTES, formatPath} from '../../constants/PathRoutes';
import api from '../../services/api';
import useDefaultRequest from '../../hooks/useDefaultRequest';
import Form from '../../components/data_entry/form/Form';
import InputFormField from '../../components/data_entry/form/InputFormField';
import TextAreaFormField
    from '../../components/data_entry/form/TextAreaFormField';
import SwitchFormField from '../../components/data_entry/form/SwitchFormField';
import RichTextFormField
    from '../../components/data_entry/form/RichTextFormField';
import ImageCropFormField
    from '../../components/data_entry/form/ImageCropFormField';
import Button from '../../components/data_entry/Button';
import Loading from '../../components/admin/Loading';
import {EyeOutlined} from "@ant-design/icons";

const FORM_NAMES = {
    TITLE: 'title',
    PREVIEW: 'preview',
    CONTENT: 'content',
    IMAGE: 'image',
    IS_ACTIVE: 'is_active',
    IS_FEATURED: 'is_featured',
    IS_TRANSPARENCY: 'is_transparency',
};

export default function AdminBlogForm() {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const {id} = useParams();
    const isEditing = !!id;

    const {data, isLoading: fetching} = useDefaultRequest({
        url: formatPath(ENDPOINTS.NEWS_DETAIL, {id}),
        makeRequest: isEditing,
        redirectRoute: ROUTES.ADMIN_BLOG_LIST
    });

    useEffect(() => {
        if (data && isEditing) {
            form.setFieldsValue({
                ...data,
                [FORM_NAMES.IMAGE]: data[FORM_NAMES.IMAGE] || null
            });
        }
    }, [data, isEditing, form]);

    const onFinish = async (values) => {
        setLoading(true);
        try {
            const formData = new FormData();

            formData.append(FORM_NAMES.TITLE, values[FORM_NAMES.TITLE]);
            formData.append(FORM_NAMES.PREVIEW, values[FORM_NAMES.PREVIEW] || '');
            formData.append(FORM_NAMES.CONTENT, values[FORM_NAMES.CONTENT]);
            formData.append(FORM_NAMES.IS_ACTIVE, values[FORM_NAMES.IS_ACTIVE] ?? true);
            formData.append(FORM_NAMES.IS_TRANSPARENCY, values[FORM_NAMES.IS_TRANSPARENCY] ?? false);
            formData.append(FORM_NAMES.IS_FEATURED, values[FORM_NAMES.IS_FEATURED] ?? false);

            // Processa a imagem
            if (values[FORM_NAMES.IMAGE] && values[FORM_NAMES.IMAGE].file) {
                formData.append(FORM_NAMES.IMAGE, values[FORM_NAMES.IMAGE].file);
            }

            if (isEditing) {
                await api.patch(formatPath(ENDPOINTS.NEWS_DETAIL, {id}), formData, {
                    headers: {'Content-Type': 'multipart/form-data'}
                });
                message.success('Notícia atualizada com sucesso');
            } else {
                await api.post(ENDPOINTS.NEWS_LIST, formData, {
                    headers: {'Content-Type': 'multipart/form-data'}
                });
                message.success('Notícia criada com sucesso');
            }
            navigate(ROUTES.ADMIN_BLOG_LIST);
        } catch (error) {
            message.error(error.response?.data?.detail || 'Erro ao salvar notícia');
        } finally {
            setLoading(false);
        }
    };

    if (fetching) {
        return <Loading />;
    }

    return (
        <Row>
            <Col xs={24}>
                <Typography.Title style={{marginTop: 0}} level={2}>
                    {isEditing ? <span>
                        Editar Notícia

                        {data.is_active && <Link
                                to={formatPath(ROUTES.BLOG_DETAIL, {slug: data.slug})}
                                target="_blank">
                             <Typography.Text type="secondary" style={{fontSize: 16}}> - Visualizar (<EyeOutlined/>)</Typography.Text>
                        </Link>}


                    </span> : 'Nova Notícia'}

                </Typography.Title>
            </Col>
            <Col xs={24}>
                <Form
                    form={form}
                    onFinish={onFinish}
                    initialValues={{
                        [FORM_NAMES.IS_ACTIVE]: true,
                        [FORM_NAMES.IS_TRANSPARENCY]: false,
                        [FORM_NAMES.IS_FEATURED]: false
                    }}
                >
                    <Row gutter={[16, 0]}>
                        <Col xs={24}>
                            <Row>
                                <Col xs={12} sm={4}>
                                    <SwitchFormField
                                        form={{
                                            label: 'Ativo',
                                            name: FORM_NAMES.IS_ACTIVE,
                                        }}
                                    />
                                </Col>
                                <Col xs={12} sm={4}>
                                    <SwitchFormField
                                        form={{
                                            label: 'Destaque',
                                            name: FORM_NAMES.IS_FEATURED,
                                            tooltip: 'Marque para destacar esta notícia na página inicial'
                                        }}
                                    />
                                </Col>
                                 <Col xs={12} sm={4}>
                                    <SwitchFormField
                                        form={{
                                            label: 'Transparência',
                                            name: FORM_NAMES.IS_TRANSPARENCY,
                                            tooltip: 'Marque para exibir esta notícia na transparencia'
                                        }}
                                    />
                                </Col>
                            </Row>
                        </Col>
                        <Col xs={8}>
                            <ImageCropFormField
                                form={{
                                    label: 'Imagem de Capa',
                                    name: FORM_NAMES.IMAGE,
                                    rules: [{
                                        required: !isEditing,
                                        message: 'Selecione uma imagem'
                                    }]
                                }}
                                aspect={16 / 9}
                                height={180}
                                placeholder="Adicionar imagem"
                            />
                        </Col>
                        <Col xs={24} lg={16}>
                            <Row>
                                <Col xs={24}>
                                    <InputFormField
                                        placeholder="Digite o título da notícia"
                                        form={{
                                            label: 'Título',
                                            name: FORM_NAMES.TITLE,
                                            rules: [{
                                                required: true,
                                                message: 'Informe o título'
                                            }]
                                        }}
                                    />
                                </Col>
                                <Col xs={24}>
                                    <TextAreaFormField
                                        rows={2}
                                        maxLength={100}
                                        showCount
                                        placeholder="Resumo da notícia (máximo 100 caracteres)"
                                        form={{
                                            label: 'Resumo',
                                            name: FORM_NAMES.PREVIEW,
                                            rules: [{
                                                required: true,
                                                message: 'Informe o preview'
                                            }, {
                                                max: 100,
                                                message: 'Máximo de 100 caracteres'
                                            }]
                                        }}
                                    />
                                </Col>
                            </Row>

                        </Col>


                        <Col xs={24}>
                            <RichTextFormField
                                minHeight="400px"
                                placeholder="Digite o conteúdo da notícia..."
                                form={{
                                    label: 'Conteúdo',
                                    name: FORM_NAMES.CONTENT,
                                    rules: [{
                                        required: true,
                                        message: 'Informe o conteúdo'
                                    }]
                                }}
                            />
                        </Col>


                        <Col xs={24}
                             style={{textAlign: 'right', marginTop: 24}}>
                            <Button
                                type="default"
                                style={{marginRight: 8}}
                                onClick={() => navigate(ROUTES.ADMIN_BLOG_LIST)}
                            >
                                Cancelar
                            </Button>
                            <Button
                                onClick={form.submit}
                                loading={loading}
                            >
                                Salvar
                            </Button>
                        </Col>
                    </Row>
                </Form>
            </Col>
        </Row>
    );
}
